import './index.scss';
import {
  ThemeProvider,
  unstable_createMuiStrictModeTheme,
} from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { BluonSearchWrapper, OriginSearchTypes } from 'bluon-search';
import { PubNubProvider } from 'pubnub-react';
import React, { useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { HashRouter } from 'react-router-dom';

import packageJson from '../package.json';

import App from './App';
import { SnackbarProvider } from './components/Snackbar/hooks';
import { PubNubUtils } from './config/pubnub';
import reportWebVitals from './reportWebVitals';
import store from './state';

import client from '@config/api';
import { AbilityContext, ability } from '@config/canAbility';
import BluonTheme from '@config/styles/theme';
import { useAppSelector } from '@hooks/state';
import useTrackGTMEvents from '@hooks/useTrackGTMEvents';
import { getStoreInfoData } from '@state/storeInfo/selectors';
import { useMasterMechanic } from '@utils/masterMechanic';

const theme = unstable_createMuiStrictModeTheme(BluonTheme);
const pubnub = PubNubUtils.getInstance().getPubNubInstance();
const environment = process ? process.env : null;

const tagManagerArgs = {
  gtmId: environment?.REACT_APP_GOOGLE_TAG_MANAGER_ID ?? 'G-2YKYTLQN96',
  auth: environment?.REACT_APP_GOOGLE_TAG_MANAGER_AUTH ?? '',
  preview: environment?.REACT_APP_GOOGLE_TAG_MANAGER_PREVIEW ?? '',
};
const feedbackIframeUrl =
  environment?.REACT_APP_FEEDBACK_IFRAME_URL ||
  'https://bluon.jotform.com/251777902873974';

TagManager.initialize(tagManagerArgs);

if (environment?.REACT_APP_ENV_NAME !== 'develop')
  Sentry.init({
    dsn: environment?.REACT_APP_SENTRY_DSN,
    environment: environment?.REACT_APP_ENV_NAME,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
    release: packageJson.version,
  });

interface DynamicBluonSearchWrapperProps {
  children: JSX.Element;
}

const DynamicBluonSearchWrapper = ({
  children,
}: DynamicBluonSearchWrapperProps) => {
  const { supplierId, email, name, phoneNumber } = useAppSelector((state) => ({
    supplierId: getStoreInfoData(state)?.id,
    email: getStoreInfoData(state)?.email,
    name: getStoreInfoData(state)?.name,
    phoneNumber: getStoreInfoData(state)?.contact_phone,
  }));

  const dynamicFeedbackUrl = useMemo(() => {
    const url = new URL(feedbackIframeUrl);
    url.searchParams.append('supplierId', supplierId ? supplierId : '');
    url.searchParams.append('email', email ? email : '');
    url.searchParams.append('name', name ? name : '');
    url.searchParams.append('phoneNumber', phoneNumber ? phoneNumber : '');

    return url.toString();
  }, [supplierId, email, name, phoneNumber]);

  return (
    <BluonSearchWrapper
      iframeUrl='search-by-part-iframe-url'
      applyStyleOverrides={true}
      axiosInstance={client}
      initTab='models'
      showTabs={true}
      apiVersion={environment?.REACT_APP_SEARCH_API_VERSION}
      applyOwnSnackbar={true}
      useTrackGTMEvents={useTrackGTMEvents}
      origin={OriginSearchTypes.ORIGIN_LIVE}
      feedbackIframeUrl={dynamicFeedbackUrl}
      useMasterMechanic={useMasterMechanic}
    >
      {children}
    </BluonSearchWrapper>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <HashRouter>
          <SnackbarProvider>
            <Sentry.ErrorBoundary>
              <PubNubProvider client={pubnub}>
                <AbilityContext.Provider value={ability}>
                  <DynamicBluonSearchWrapper>
                    <App />
                  </DynamicBluonSearchWrapper>
                </AbilityContext.Provider>
              </PubNubProvider>
            </Sentry.ErrorBoundary>
          </SnackbarProvider>
        </HashRouter>
      </ThemeProvider>
    </Provider>
  </React.StrictMode>,
);

reportWebVitals();
